import { useCallback, useEffect, useRef, useState } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'

interface UseMeasuredNoteHeightOptions {
  noteId: string
  /** Die Höhe, wie sie in der Datenbank steht — die Schwelle, gegen die gemessen wird. */
  storedHeight: number
  /**
   * Ob überhaupt gemessen werden soll. `false` für einen **Notizen**, dessen
   * Höhe gezogen wird, und in Stories und Tests ohne Datenbank.
   */
  enabled: boolean
}

/**
 * Wie lange die Messungen ruhen müssen, bevor geschrieben wird.
 *
 * Beim Tippen fällt eine Messung je Anschlag an, und jeder Schreibvorgang
 * stößt die Sync-Warteschlange an — ein Satz getippter Wörter erzeugte sonst
 * ein Dutzend Einträge für dasselbe Feld. Dieselbe Vorsicht wie bei
 * `onSplitRatioCommit` in `ProjectCanvasPanel`.
 */
const SETTLE_MS = 200

/**
 * Ab welcher Abweichung überhaupt geschrieben wird.
 *
 * **Der Riegel gegen die Rückkopplung.** Der Weg ist: schreiben → Live-Query →
 * neu zeichnen → messen → schreiben. Ohne Schwelle liefe eine Notiz endlos,
 * sobald zwei Messungen um ein halbes Pixel auseinanderliegen — und das tun
 * sie, weil Schriftrendering nicht auf ganze Pixel fällt.
 */
const HEIGHT_EPSILON = 1

/**
 * Die Höhe einer **Notiz** messen und zurückschreiben.
 *
 * **Warum sie überhaupt in der Datenbank steht, obwohl sie aus dem Inhalt
 * entsteht:** Einrasten, **Maßklammern**, **Randpfeile**, die
 * **Lesereihenfolge** und die **Übernahme** rechnen alle mit `width`/`height`
 * und dürfen nicht wissen, woher die Zahl kommt. Eine Art, die ihre Höhe
 * verschwiege, fiele aus jeder dieser Rechnungen heraus.
 *
 * **Beim Zeichnen gewinnt die Messung, nicht der gespeicherte Wert.** Die Zahl
 * in der Datenbank dient allein den anderen Rechnungen, nie dem Layout — sonst
 * zuckte die Notiz beim Laden auf die zuletzt gemerkte Höhe, bevor der Text
 * sie neu bestimmt.
 *
 * **Sie wird NICHT synchronisiert**, und das ist der Kern: Sie ist eine
 * Messung DIESES Geräts, keine Entscheidung des Nutzers — genau wie der
 * **Ausschnitt**, der aus demselben Grund lokal bleibt (CONTEXT.md). Zwei
 * Geräte mit anderem Schriftrendering messen 181 und 182 Pixel; trüge der
 * Sync die Werte hin und her, schrieben beide abwechselnd zurück und die
 * Notiz zappelte. Deshalb geht die Messung an der Sync-Warteschlange vorbei
 * (`setCanvasNoteMeasuredHeight`), und der Pull lässt die Höhe einer Notiz
 * unangetastet.
 *
 * @example
 * const measureRef = useMeasuredNoteHeight({ noteId: note.id, storedHeight: note.height, enabled: true })
 * <div ref={measureRef}>…</div>
 */
export function useMeasuredNoteHeight({
  noteId,
  storedHeight,
  enabled,
}: UseMeasuredNoteHeightOptions): (element: HTMLElement | null) => void {
  const dataSource = useDataSource()
  const [element, setElement] = useState<HTMLElement | null>(null)

  // Über ein Ref gelesen: Der Beobachter soll nicht bei jeder neuen Höhe ab-
  // und wieder angemeldet werden — das ist genau der Fall, der beim Tippen
  // ununterbrochen einträte.
  const storedRef = useRef(storedHeight)
  storedRef.current = storedHeight

  const measureRef = useCallback((next: HTMLElement | null) => {
    setElement(next)
  }, [])

  useEffect(() => {
    if (!enabled || !element) return

    let timer: ReturnType<typeof setTimeout> | undefined

    const commit = () => {
      const height = element.offsetHeight
      /*
        Eine Null kommt aus einem Tab hinter `display: none` (CLAUDE.md,
        TabStack-Falle). Sie zu schreiben verlöre die echte Höhe, und die
        Notiz käme beim Zurückwechseln als Strich zurück.
      */
      if (height <= 0) return
      if (Math.abs(height - storedRef.current) < HEIGHT_EPSILON) return
      // NICHT `updateCanvasNote`: Das stellt einen Sync-Eintrag ein, und die
      // Höhe gehört nicht in die Cloud (siehe den Kopfkommentar).
      void dataSource.setCanvasNoteMeasuredHeight(noteId, height)
    }

    const observer = new ResizeObserver(() => {
      clearTimeout(timer)
      timer = setTimeout(commit, SETTLE_MS)
    })
    observer.observe(element)

    return () => {
      clearTimeout(timer)
      observer.disconnect()
    }
  }, [enabled, element, noteId, dataSource])

  return measureRef
}
